import { defineStore } from "pinia";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import { useAuthStore } from "./auth.store";
import { isSG } from "@/config/appVersion";

// Store do obsługi danych aktualnie zalogowanego użytkownika oraz preferencji
export const useUserStore = defineStore({
  id: "user",
  state: () => ({
    admin: null,          // Czy aktualny użytkownik ma uprawnienia admina?
    currentUser: null,    // Dane zalogowanego użytkownika
    preferences: null,    // Preferencje użytkownika
    vehicleListDisplayProperties: [], // Dostępne właściwości listy pojazdów do wyboru
    _vehicleListPropsEndpointAvailable: null, // null=nieznane, false=brak endpointu (404), true=dostępny
    _selfPromise: null,
  }),
  actions: {
    // Resetowanie stanu użytkownika (wylogowanie, czyszczenie preferencji)
      reset() {
          this.admin = null;
          this.currentUser = null;
          this.preferences = null;
          this.vehicleListDisplayProperties = [];
          this._loadedForToken = null;
          this._vehicleListPropsEndpointAvailable = null;
          this._selfPromise = null;
      },
    // Aktualizacja danych użytkownika (np. imię, email)
    async update(payload) {
      try {
        const updateRes = await fetchWrapper.put(
          `${process.env.VUE_APP_API_URL}/users/self/update`,
          payload
        );
        return { result: updateRes, error: null };
      } catch (error) {
        console.log(error);
        return { result: null, error: error };
      }
    },
    // Aktualizacja ustawień/preferencji użytkownika
    async updateSettings(payload) {
      try {
        const updateRes = await fetchWrapper.put(
          `${process.env.VUE_APP_API_URL}/users/self/settings`,
          payload
        );
        this.preferences = updateRes;
        return { result: updateRes, error: null };
      } catch (error) {
        console.log(error);
        return { result: null, error: error };
      }
    },
    // Zmiana hasła przez użytkownika
    async updatePassword(payload) {
      try {
        const updateRes = await fetchWrapper.put(
          `${process.env.VUE_APP_API_URL}/users/self/update-password`,
          payload
        );
        return { result: updateRes, error: null };
      } catch (error) {
        console.log(error);
        return { result: null, error: error };
      }
    },
    // Pobierz własne dane i preferencje użytkownika (jeśli jeszcze nie są załadowane)
      /**
       * force=true -> zawsze pobierz z API
       * force=false -> pobierz tylko jeśli nie ma danych albo token się zmienił
       */
      async self(force = false) {
          const auth = useAuthStore();
          if (!auth.token) {
              this.reset();
              return null;
          }

          if (!force && this._selfPromise) {
              return this._selfPromise;
          }

          const tokenChanged = this._loadedForToken !== auth.token;

          if (!force && this.currentUser && !tokenChanged) {
              return this.currentUser;
          }

          // jeśli token się zmienił, wyczyść stan zanim pobierzesz
          if (tokenChanged) {
              this.reset();
          }

          this._selfPromise = (async () => {
              const userRes = await fetchWrapper.get(
                  `${process.env.VUE_APP_API_URL}/users/self`
              );

              // UWAGA: nie mutuj obiektu z backendu w miejscu, zrób kopię
              const u = { ...userRes };

              if (u.phoneNumber) {
                  u.phoneNumberDir = u.phoneNumber.substring(0, 3);
                  u.phoneNumber = u.phoneNumber.substring(3);
              } else {
                  u.phoneNumberDir = "+48";
                  u.phoneNumber = "";
              }

              const prefRes = await fetchWrapper.get(
                  `${process.env.VUE_APP_API_URL}/users/self/settings`
              );

              let metricsRes =
                  prefRes?.vehicleListDisplayProperties ??
                  prefRes?.vehicleListProperties;

              if (!Array.isArray(metricsRes) && this._vehicleListPropsEndpointAvailable !== false) {
                  try {
                      metricsRes = await fetchWrapper.get(
                          `${process.env.VUE_APP_API_URL}/users/self/settings/vehicle-list-properties`
                      );
                      this._vehicleListPropsEndpointAvailable = true;
                  } catch (e) {
                      metricsRes = [];
                      if (e === 404 || e === "404") {
                          this._vehicleListPropsEndpointAvailable = false;
                      }
                  }
              }

              if (!Array.isArray(metricsRes)) {
                  metricsRes = [];
              }

              this.currentUser = u;
              this.preferences = prefRes;
              this.vehicleListDisplayProperties = Array.isArray(metricsRes) ? metricsRes : [];
              this.admin = null; // przelicz role od nowa
              this._loadedForToken = auth.token;

              return this.currentUser;
          })();

          try {
              return await this._selfPromise;
          } finally {
              this._selfPromise = null;
          }
      },
    // Sprawdź i ustaw, czy użytkownik ma uprawnienia administratora (ROLE_7)
    async roles() {
      if (this.currentUser === null) {
        await this.self();
      }

      if (!this.currentUser || !this.currentUser.roles) {
        this.admin = false;
        return;
      }

      if (this.admin === null) {
        const hasRole7 = this.currentUser.roles[0]?.permissions?.some(
          (perm) => perm.name === "ROLE_7"
        );
        this.admin = !!hasRole7;
      }
    },
    // Sprawdzenie czy użytkownik ma dostęp do określonych zasobów/funkcjonalności (np. WantedList, Stat itd.)
    hasAccess(items) {
      this.roles(); // Upewnij się, że role są wczytane

      const itemArr = items.split(",");

      for (const itemCheck in itemArr) {
        if (this.getItemAccess(itemArr[itemCheck])) {
          return true; // Ma dostęp do przynajmniej jednej funkcji
        }
      }
      return false;
    },
    // Mapowanie nazw uprawnień do konkretnych ról systemowych (np. ROLE_13 = BlackList)
    getItemAccess(item) {
      if (item === "CurrentMeasurements") {
        // Umożliwia dostęp do zasobów odpowiedzialnych za bieżące pomiary.
        return this.checkRole("ROLE_1");
      }
      if (item === "ArchiveMeasurements") {
        // Umożliwia dostęp do zasobów odpowiedzialnych za archiwalne pomiary.
        return this.checkRole("ROLE_2");
      }
      if (item === "Stat") {
        // Umożliwia dostęp do zasobów odpowiedzialnych za statystyki.
        return this.checkRole("ROLE_4");
      }
      if (item === "UsersAdmin") {
        // Umożliwia przeglądanie logów aktywności wszystkich zwykłych użytkowników za wyjątkiem użytkowników specjalnych.
        return this.checkRole("ROLE_7");
      }
      if (item === "SelfPersonal") {
        // Umożliwia wyświetlenie danych personalnych użytkownika.
        return this.checkRole("ROLE_10");
      }
      if (item === "PasswordChange") {
        // Umożliwia użytkownikowi zmianę jego hasła
        return this.checkRole("ROLE_11");
      }
      if (item === "Roles") {
        // Umożliwia dostęp do wszystkich ról, które istnieją w systemie, za wyjątkiem tych specjalnych, które są przeznaczone dla specjalnych użytkowników.
        return this.checkRole("ROLE_19");
      }
      if (item === "Vms") {
        // Umożliwia dostęp do zasobów, które zwracają dane stacji preselekcji.
        // return this.checkRole("ROLE_27");
        return this.checkRole("ROLE_3");
      }
      if (item === "SelfConfig") {
        // Umożliwia dostęp do zasobów, które zapisują i odczytują parametry konfiguracyjne zalogowanego użytkownika..
        // return this.checkRole("ROLE_30");
        return this.checkRole("ROLE_10") || this.checkRole("ROLE_11");
      }
      if (item === "Anonymization") {
        // Umożliwia wyłączenie nietrwałej anonimizacji dla użytkowników posiadających to uprawnienie.
        return this.checkRole("ROLE_37");
      }
      if (item === "WantedList") {
        // Globalne uprawnienie LUB członkostwo w grupie kontaktów (dziedziczone przez
        // dopasowany Contact -> User po e-mailu / numerze telefonu).
        return this.checkRole("ROLE_12") || this.isWantedListGroupMember();
      }

      /*
        Role - panel admina:
        7, 8, 12, 17, 19, 20, 22, 27, 33, 34
      */

      if (item === 'Admin') {
        return this.checkRole("ROLE_7") || this.checkRole("ROLE_8") || this.checkRole("ROLE_12") || this.checkRole("ROLE_13") || this.checkRole("ROLE_14") || this.checkRole("ROLE_15") || this.checkRole("ROLE_17") || this.checkRole("ROLE_19") || this.checkRole("ROLE_20") || this.checkRole("ROLE_22") || this.checkRole("ROLE_27") || this.checkRole("ROLE_33") || this.checkRole("ROLE_34") || this.checkRole("ROLE_38");
      }

      if (item === 'UserManagement') {
        // Ścisłe "zarządzanie kontami użytkowników" (CRUD userów). ROLE_19 = listing
        // zwykłych ról, ROLE_20 = listing wszystkich ról (włącznie ze specjalnymi).
        return this.checkRole("ROLE_19") || this.checkRole("ROLE_20");
      }

      if (item === 'UserActivity') {
        // Historia aktywności użytkowników — ROLE_7 (zwykli) lub ROLE_8 (wszyscy,
        // włącznie ze specjalnymi). Bez ROLE_7/8 menu „Aktywność użytkowników"
        // ma być ukryte — sam ROLE_19 (user manager) tego NIE zapewnia.
        return this.checkRole("ROLE_7") || this.checkRole("ROLE_8");
      }

      if (item === 'RoleManagement') {
        // Pełne zarządzanie grupami ról (CRUD ról i ich uprawnień). Wymaga ROLE_20
        // — czyli adminów z wglądem w *wszystkie* role, łącznie ze specjalnymi.
        // ROLE_19 daje tylko prawo przeglądania regularnych ról na potrzeby
        // przypisania userowi (dropdown w AddUser/EditUser) — to za mało, żeby
        // wchodzić do widoku zarządzania samymi rolami.
        return this.checkRole("ROLE_20");
      }

      if (item === 'Tickets') {
        return this.checkRole("ROLE_38");
      }

      if (item === 'LocationManagement') {
        // Zarządzanie stacjami preselekcji (CRUD lokalizacji + pasów ruchu). Dedykowane
        // uprawnienie zapisu ROLE_41 — niezależne od odczytowego ROLE_29. Musi być spójne
        // z PermissionCode::LOCATION_MANAGEMENT na BE. Dodatkowo w routingu/menu wymagana
        // jest włączona flaga funkcji (configStore.featureFlags.locationManagement).
        return this.checkRole("ROLE_41");
      }

      if (item === 'LocationStatuses') {
        // Widoczne dla administratorów: ROLE_7 (zwykli) lub ROLE_8 (wszyscy, włącznie ze
        // specjalnymi). Musi być spójne z LocationStatusController na BE.
        return this.checkRole("ROLE_7") || this.checkRole("ROLE_8");
      }

      if (item === 'BlackList') {
        // Globalne uprawnienie (ROLE_13) LUB member-by-group (read + dodawanie własnych tablic).
        return this.checkRole("ROLE_13") || this.isWantedListGroupMember();
      }

      if (item === 'WhiteList') {
        // Tylko globalne uprawnienie (ROLE_15). Member-by-group NIE dostaje wglądu do białej listy
        // — przynależność do grupy kontaktów daje wyłącznie dostęp do czarnej listy.
        return this.checkRole("ROLE_15");
      }

      if (item === 'Contacts') {
        // Tylko globalne uprawnienie (ROLE_14). Member-by-group NIE widzi listy kontaktów —
        // gdyby widział, mógłby przeglądać dane kontaktowe innych członków swojej grupy.
        return this.checkRole("ROLE_14");
      }

      return false;
    },
    // Sprawdź, czy aktualny użytkownik ma określoną rolę (np. ROLE_13, ROLE_7)
    checkRole(id) {
      return this.currentUser?.roles?.[0]?.permissions?.some(
        (role) => role.name === id
      ) || false;
    },
    // Czy zalogowany użytkownik jest członkiem grupy kontaktów list poszukiwanych
    // (przez dopasowany Contact::linkedUser). Flaga liczona po stronie BE,
    // przesyłana w odpowiedzi /users/self.
    isWantedListGroupMember() {
      return this.currentUser?.wantedListGroupMember === true;
    },
    // Czy zalogowany użytkownik ma globalne uprawnienie modyfikacji list (white/black).
    // Member-by-group nie spełnia tego warunku — może edytować tylko własne tablice.
    canModifyWantedListsGlobally() {
      return this.checkRole("ROLE_13") || this.checkRole("ROLE_15");
    },
    // Czy zalogowany użytkownik może edytować/usunąć konkretną tablicę poszukiwaną.
    //
    // Tablica nie ma już "właściciela" — jedyną podstawą edycji w trybie member-by-group
    // jest członkostwo w grupie kontaktów spiętej z tą tablicą. Globalne uprawnienie
    // (ROLE_13 / ROLE_15) przepuszcza zawsze.
    //
    // Sygnatura: { contactGroupIds: number[] } (z mapVehicleFromApi).
    canEditWantedPlate(plate) {
      if (this.canModifyWantedListsGlobally()) {
        return true;
      }
      const plateGroups = Array.isArray(plate?.contactGroupIds) ? plate.contactGroupIds : [];
      const myGroups = Array.isArray(this.currentUser?.wantedListContactGroupIds)
        ? this.currentUser.wantedListContactGroupIds
        : [];
      if (plateGroups.length === 0 || myGroups.length === 0) {
        return false;
      }
      return plateGroups.some(id => myGroups.includes(id));
    },
    // Czy zalogowany użytkownik może zarządzać kontaktami (CRUD). Member nie może.
    canManageContactsGlobally() {
      return this.checkRole("ROLE_14");
    },
    // Uprawnienia chronione — używane WYŁĄCZNIE w flow tworzenia/edycji grupy ról
    // (AddRole / EditRole). Admin bez ROLE_13/14/15 nie może wrzucić tych uprawnień
    // do nowej/edytowanej grupy (bo wtedy mógłby sobie tę grupę przypisać i obejść
    // ograniczenie).
    //
    // Wyjątek: ROLE_20 (full role-manager — wgląd we WSZYSTKIE grupy, łącznie ze
    // specjalnymi) ma carte blanche. Inaczej super-admin nie mógłby przywrócić
    // chronionego uprawnienia, które wcześniej zostało skasowane z grupy ról,
    // o ile sam go w zestawie nie ma.
    //
    // Ta lista NIE rządzi już dropdownem ról przy AddUser/EditUser — tam decyduje
    // hierarchia po NAZWIE grupy (`canAssignRoleGroup`).
    //
    // Lista MUSI być spójna z `Permissions::RESTRICTED_GRANT_PERMISSIONS` na BE.
    canGrantPermission(permissionName) {
      if (this.checkRole("ROLE_20")) {
        return true;
      }
      const restricted = ["ROLE_13", "ROLE_14", "ROLE_15"];
      if (!restricted.includes(permissionName)) {
        return true;
      }
      return this.checkRole(permissionName);
    },
    // Docelowa ścieżka po zalogowaniu — zależy od wariantu wdrożenia i uprawnień usera.
    //
    // Wariant SG ma ukryty widok "/" (CurrentMeasurements), więc domyślnie wpadamy na
    // "/archival". Jeśli jednak user nie ma `ArchiveMeasurements` (np. „administrator
    // abz" z samym ROLE_19), próbujemy kolejnych sensownych miejsc — najpierw widoków
    // zarządzania, na końcu Preferencji (każdy zalogowany user ma ROLE_10/ROLE_11).
    //
    // W non-SG zawsze "/" — domyślny widok bieżących pomiarów.
    landingPath() {
      if (!isSG) {
        return "/";
      }
      if (this.getItemAccess("ArchiveMeasurements")) return "/archival";
      if (this.getItemAccess("UserManagement")) return "/user-management";
      if (this.getItemAccess("UserActivity"))   return "/user-activity";
      if (this.getItemAccess("RoleManagement")) return "/role-group-management";
      if (this.getItemAccess("Tickets"))        return "/tickets";
      if (this.getItemAccess("WantedList"))     return "/black-list";
      if (this.getItemAccess("Vms"))            return "/vms";
      if (this.getItemAccess("Stat"))           return "/stat";
      if (this.getItemAccess("LocationStatuses")) return "/location-statuses";
      // Ostatni fallback — każdy zalogowany user ma dostęp do własnych preferencji.
      return "/preference";
    },
    // Czy admin może przypisać innemu użytkownikowi tę grupę ról.
    //
    // Hierarchia jest po NAZWIE grupy, nie po podzbiorach uprawnień — admin „abz"
    // (4 uprawnienia) musi móc nadać „operatora 1" (kilkanaście uprawnień
    // operatorskich, których sam nie posiada). Reguły (sprawdzane w kolejności):
    //
    //   1. ROLE_20 (full role-manager) — carte blanche, każda grupa.
    //   2. Własna grupa (porównanie po nazwie, case-insensitive) — admin nadaje swoją.
    //   3. Hierarchia tierów administratorskich (patrz `adminRank` niżej):
    //      „administrator" stoi NAD „administrator bez list" / „administrator abz".
    //      Wyższy tier może nadawać role ściśle niższych tierów. Dwa niższe tiery
    //      są równorzędne — NIE widzą siebie nawzajem ani „administrator".
    //   4. Nazwa docelowej grupy zawiera „operator" (case-insensitive) — operatorzy
    //      są zawsze poniżej dowolnego admina w hierarchii.
    //   5. Inaczej — odrzuć.
    //
    // Skutki:
    //   ✓ „administrator" → „administrator bez list", „administrator abz", operatorzy
    //   ✗ „administrator bez list" / „administrator abz" → „administrator" (tier wyżej)
    //   ✗ „administrator bez list" ↔ „administrator abz" (ten sam tier)
    //
    // Musi być spójne z `backend/src/Security/Permissions.php#canAssignRoleGroup()`.
    canAssignRoleGroup(role) {
      if (this.checkRole("ROLE_20")) {
        return true;
      }
      const targetName = String(role?.name ?? "").trim().toLowerCase();
      if (!targetName) {
        return false;
      }
      const myRoleName = String(this.currentUser?.roles?.[0]?.name ?? "").trim().toLowerCase();
      if (myRoleName && targetName === myRoleName) {
        return true;
      }
      // Ranga w hierarchii admina (wyższa = wyżej). Musi być spójne z BE `Permissions::adminRank()`.
      const adminRank = (name) => {
        if (name === "administrator") return 2;
        if (name === "administrator bez list" || name === "administrator abz") return 1;
        return 0;
      };
      const myRank = adminRank(myRoleName);
      const targetRank = adminRank(targetName);
      if (myRank > 0 && targetRank > 0 && myRank > targetRank) {
        return true;
      }
      if (targetName.includes("operator")) {
        return true;
      }
      return false;
    },
  },
});
